import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, throwError } from 'rxjs';
import { mapTo, take, tap } from 'rxjs/operators';
import { YesNoModalDialogComponent } from '../../components/yes-no-modal-dialog/yes-no-modal-dialog.component';
import { YesNoModalDialogModel } from '../../components/yes-no-modal-dialog/yes-no-modal-dialog.model';
import { ConfigConstants } from '../../config/config-constants';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ConfigService } from '../config/config.service';
import { TimesheetAndExpensesService } from './timesheet-and-expenses.service';
import { ModalClass } from '../../enums/modal-bootstrap-classes.enum';

@Injectable({
    providedIn: 'root',
})
export class StartWorkService {
    private warningBreakTimeHoursAcknowledged = false;
    public constructor(
        private readonly timesheetAndExpensesService: TimesheetAndExpensesService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly translate: TranslateService,
        private readonly modalService: BsModalService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly configService: ConfigService,
    ) {}

    /**
     * This method saves start of work updated by user from frequent activities screen or from details screen
     * It will also validate the SOW date and time and throw warning popup if it is invalid
     *
     * @param startOfWork
     */
    public setStartOfWork(startOfWork: moment.Moment): Observable<void> {
        const submittedTimesheet = this.timesheetAndExpensesService.getSubmittedTimesheet();
        const endOfWork = submittedTimesheet.getEndOfWork();
        const dateTimeFormat = this.clientManager.getDateTimeFormat();

        // end-of-work should always be before a new start-of-work
        const showWarningEowAfterSow = endOfWork.isAfter(startOfWork);

        if (showWarningEowAfterSow) {
            this.log.debug(
                `StartWorkService: setStartOfWork: Displaying warning about EoW (${endOfWork.toString()}) being after SoW (${startOfWork.toString()})`,
            );

            const errorText = <string>this.translate.instant('SOW_BEFORE_EOW_TEXT', {
                endOfWork: endOfWork.format(dateTimeFormat),
                startOfWork: startOfWork.format(dateTimeFormat),
            });

            const warningEowAfterSowModalInstance = this.modalService.show(YesNoModalDialogComponent, {
                class: ModalClass.MODAL_MEDIUM,
                initialState: {
                    model: new YesNoModalDialogModel(
                        <string>this.translate.instant('SOW_BEFORE_EOW_TITLE'),
                        errorText,
                        <string>this.translate.instant('SOW_BEFORE_EOW_OK_LABEL'),
                    ),
                },
                ariaLabelledBy: 'yes-no-modal-title',
            });

            warningEowAfterSowModalInstance.content?.modalResult.subscribe(() => {
                this.log.debug(
                    `StartWorkService: setStartOfWork: Warning about EoW after SoW was acknowledged by the user`,
                );
            });

            return throwError(`${ConfigConstants.TravelStatusErrors.INVALID_START_OF_WORK_ERROR}`);
        }

        // check for 11h break between EoW and SoW
        const breakTimeHours = this.configService.getEowSowBreakHours();

        // if a break time is configured and we have a previous timesheet, we can check
        const showWarningBreakTimeHours =
            breakTimeHours > 0 &&
            startOfWork.diff(endOfWork, 'minutes') < breakTimeHours * ConfigConstants.timeMultiplier;

        if (showWarningBreakTimeHours && !this.warningBreakTimeHoursAcknowledged) {
            this.log.debug(
                `StartWorkService: setStartOfWork: Displaying warning about less than 11h between EoW (${endOfWork.toString()}) and SoW (${startOfWork.toString()})`,
            );
            const breakWarningText = <string>this.translate.instant('EOW_SOW_BREAK_WARNING_TEXT', {
                endOfWork: endOfWork.format(dateTimeFormat),
                startOfWork: startOfWork.format(dateTimeFormat),
                breakHours: breakTimeHours,
            });

            const warningBreakTimeHoursModalInstance = this.modalService.show(YesNoModalDialogComponent, {
                class: ModalClass.MODAL_MEDIUM,
                initialState: {
                    model: new YesNoModalDialogModel(
                        <string>this.translate.instant('EOW_SOW_BREAK_WARNING_TITLE'),
                        breakWarningText,
                        <string>this.translate.instant('EOW_SOW_BREAK_WARNING_OK_LABEL'),
                    ),
                },
                ariaLabelledBy: 'yes-no-modal-title',
            });

            warningBreakTimeHoursModalInstance.content?.modalResult.subscribe(() => {
                this.log.debug(
                    `StartWorkService: setStartOfWork: Warning about 11h break was acknowledged by the user`,
                );
                this.warningBreakTimeHoursAcknowledged = true;
            });

            return throwError(`${ConfigConstants.TravelStatusErrors.START_OF_WORK_BREAK_WARNING}`);
        }

        return this.timesheetAndExpensesService.saveStartOfWork(startOfWork).pipe(
            take(1),
            tap(() => {
                this.log.debug(`StartWorkService: setStartOfWork: set startOfWork: ${startOfWork}`);
                this.warningBreakTimeHoursAcknowledged = false;
            }),
            mapTo(undefined),
        );
    }
}
